/**
 * V98 — "Mis facturas" (Configuración → Facturación del TENANT).
 * El contenido de las facturas de venta (folio/total/receptor/error) es DEL
 * TENANT: esta vista RLS-scoped lo muestra SOLO a él (permiso cfdi_sales:view),
 * con Reintentar en las failed (cfdi_sales:emit) y descarga XML/PDF. El
 * platform de FluidSol ya NO ve este contenido — solo estatus y conteos.
 * Se auto-oculta si el módulo/permiso no aplica (la carga falla) o si el
 * tenant aún no tiene CFDIs (cero ruido para quien no factura).
 */
import { useEffect, useState } from 'react';
import { api, apiDownload } from '../lib/api';
import { SalesCfdiDialog } from './SalesCfdiDialog';

interface CfdiRow {
  id: string;
  source_type?: 'order' | 'booking' | 'global' | 'egreso';
  status: 'pending' | 'needs_data' | 'stamped' | 'failed';
  uuid: string | null;
  total: string | number;
  currency: string;
  receptor_rfc: string | null;
  receptor_razon_social: string | null;
  timbrado_at: string | null;
  email_sent_at: string | null;
  error: string | null;
  has_xml: boolean;
  has_pdf: boolean;
  created_at: string;
}

const STATUS_CLS: Record<string, string> = {
  stamped: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-rose-50 text-rose-700',
  needs_data: 'bg-amber-50 text-amber-700',
  pending: 'bg-slate-100 text-slate-600',
};
const STATUS_LABEL: Record<string, string> = {
  stamped: 'Timbrada',
  failed: 'Falló',
  needs_data: 'Faltan datos',
  pending: 'Pendiente',
};

const PAGE_SIZE = 20;

export function SalesCfdiListCard({ canRetry, standalone = false }: {
  canRetry: boolean;
  /** true = sección propia (Finanzas → Mis facturas): SIEMPRE visible, incluso vacía. */
  standalone?: boolean;
}) {
  const [items, setItems] = useState<CfdiRow[]>([]);
  const [visible, setVisible] = useState(standalone);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  // Errores del PAC expandidos (por id): la causa real suele venir al final del
  // mensaje (anidada tras un "401 -"), así que hay que poder ver el texto COMPLETO.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleError = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  // Follow-up V98: Reintentar abre el dialog PRE-LLENADO para re-capturar el
  // receptor (un fallo por RFC/razón mal capturados se corrige ahí mismo).
  const [retrying, setRetrying] = useState<CfdiRow | null>(null);

  const load = (status: string) =>
    api<{ items: CfdiRow[] }>(`/cfdi-sales${status ? `?status=${status}` : ''}`).then((r) => {
      setItems(r.items ?? []);
      // Visible desde que el tenant tiene AL MENOS un CFDI (cualquier filtro).
      if ((r.items ?? []).length > 0 || status) setVisible(true);
    });

  useEffect(() => {
    setPage(0);
    load(filter).catch((e: any) => {
      // Embebida: sin módulo/permiso la card no existe. Standalone: se queda y avisa.
      if (standalone) setErr(e?.body?.message || 'No se pudieron cargar tus facturas.');
      else setVisible(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  if (!visible) return null;

  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const pageItems = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-100 px-6 py-5 md:px-8">
        <div>
          <h3 className="font-serif text-lg tracking-tight text-slate-900">Mis facturas (CFDI)</h3>
          <p className="text-sm text-slate-500">
            Facturas emitidas a tus clientes desde el punto de venta. Solo tu negocio ve este detalle.
          </p>
          {/* Cancelación diferida (in-app = siguiente concern): fija expectativas. */}
          <p className="mt-1 text-xs text-slate-400">
            La cancelación de un CFDI se realiza con tu contador (tiene implicaciones fiscales: motivo,
            sustitución y plazos); por ahora no se cancela desde la app.
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-[11px]">
          {['', 'stamped', 'failed', 'needs_data'].map((s) => (
            <button key={s || 'all'} onClick={() => setFilter(s)}
              className={`rounded px-2 py-0.5 ${filter === s ? 'bg-slate-900 text-white' : 'text-slate-500'}`}>
              {s === '' ? 'Todas' : STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      </div>
      <div className="px-6 py-4 md:px-8">
        {err && <p className="mb-2 text-sm text-rose-600">{err}</p>}
        <div className="max-h-[28rem] overflow-x-auto overflow-y-auto rounded-xl border border-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Folio fiscal</th><th className="px-3 py-2">Receptor</th>
                <th className="px-3 py-2">Total</th><th className="px-3 py-2">Detalle</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageItems.map((c) => (
                <tr key={c.id}>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {new Date(c.created_at).toLocaleDateString('es-MX')}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_CLS[c.status]}`}>
                      {STATUS_LABEL[c.status] ?? c.status}
                    </span>
                    {/* V110/V116 — tipo especial: global (público general) / egreso (nota de crédito). */}
                    {c.source_type === 'global' && (
                      <span className="ml-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700">Global</span>
                    )}
                    {c.source_type === 'egreso' && (
                      <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">Egreso</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">{c.uuid ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className="font-mono">{c.receptor_rfc ?? '—'}</span>
                    {c.receptor_razon_social && (
                      <span className="block max-w-[180px] truncate text-slate-500">{c.receptor_razon_social}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    ${Number(c.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2 align-top text-xs text-rose-600">
                    {c.error ? (
                      <div className="max-w-[280px]">
                        <button type="button" onClick={() => toggleError(c.id)}
                          className="text-left underline decoration-dotted underline-offset-2 hover:text-rose-700">
                          {expanded.has(c.id)
                            ? 'Ocultar detalle'
                            : (c.error.length > 60 ? `${c.error.slice(0, 60)}…` : c.error)}
                        </button>
                        {expanded.has(c.id) && (
                          <div className="mt-1">
                            {/* Mensaje COMPLETO del PAC (para diagnosticar la causa real). */}
                            <pre className="max-h-44 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-rose-200 bg-rose-50 p-2 text-[11px] leading-relaxed text-rose-700">{c.error}</pre>
                            <button type="button" onClick={() => navigator.clipboard?.writeText(c.error ?? '')}
                              className="mt-1 rounded-lg border border-slate-200 bg-white px-2 py-0.5 text-[10px] text-slate-600 hover:bg-slate-50">
                              Copiar error
                            </button>
                          </div>
                        )}
                      </div>
                    ) : ''}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1.5">
                      {c.has_xml && (
                        <button onClick={() => apiDownload(`/cfdi-sales/${c.id}/download/xml`, `CFDI-${c.uuid ?? c.id}.xml`)}
                          className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50">
                          XML
                        </button>
                      )}
                      {c.has_pdf && (
                        <button onClick={() => apiDownload(`/cfdi-sales/${c.id}/download/pdf`, `CFDI-${c.uuid ?? c.id}.pdf`)}
                          className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50">
                          PDF
                        </button>
                      )}
                      {canRetry && (c.status === 'failed' || c.status === 'needs_data') && (
                        <button onClick={() => { setErr(null); setRetrying(c); }}
                          className="rounded-lg bg-slate-900 px-2 py-1 text-[11px] font-medium text-white hover:bg-slate-700">
                          Reintentar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-400">Sin facturas con este filtro.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="mt-3 flex items-center justify-end gap-2 text-xs text-slate-500">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
              className="rounded-lg border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-40">←</button>
            <span>Página {page + 1} de {pages}</span>
            <button onClick={() => setPage(Math.min(pages - 1, page + 1))} disabled={page >= pages - 1}
              className="rounded-lg border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-40">→</button>
          </div>
        )}
      </div>

      {retrying && (
        <SalesCfdiDialog
          retryCfdiId={retrying.id}
          prefill={{ rfc: retrying.receptor_rfc, razonSocial: retrying.receptor_razon_social }}
          onClose={() => { setRetrying(null); load(filter).catch(() => {}); }}
        />
      )}
    </div>
  );
}
